import { useEffect, useState } from "react";
import Container from "../Container";
import PlanCard from "../cards/PlanCard";
import firebase from "../../config/fbConfig";
import SpinningLoader from "../loader/spinning_loader";
import TeamPlanCard from "../cards/TeamPlanCard";

const PlansSection = ({ bgImage, CTA, createUserAndCheckout1, createUserAndCheckout2, createUserAndCheckout3, discount_code }) => {
  // State Variables
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardOrder, setCardOrder] = useState((CTA === "Jetzt anmelden") ? ["Creator", "Starter", "Kostenlos"] : ["Starter", "Creator"]);
  const [discountInfo, setDiscountInfo] = useState(null);
  // Set to false for monthly pricing and remove any toggle functionality
  const enabled = false;

  // Functions and Event Handlers
  const getPlanByRole = (currentRole) => {
    return Object.values(products).find((plan) => plan?.name === currentRole && plan?.active);
  };

  const getPricesByPeriod = (period, prices) => {
    return prices?.find(price => price.priceData.interval === period && price.priceData.active === true);
  };

  const calculateDiscountedPrice = (originalPrice) => {
    if (!discountInfo || !discountInfo.active) {
      return formatPrice(originalPrice);
    }
    let discountedPrice;
    if (discountInfo.percent_off) {
      discountedPrice = originalPrice * (1 - discountInfo.percent_off / 100);
    } else if (discountInfo.amount_off) {
      discountedPrice = Math.max(0, originalPrice - discountInfo.amount_off / 100);
    } else {
      discountedPrice = originalPrice;
    }

    return formatPrice(discountedPrice);
  };

  const formatPrice = (price) => {
    const roundedPrice = Math.round(price * 100) / 100;
    return roundedPrice % 1 === 0 ? roundedPrice.toString() : roundedPrice.toFixed(2);
  };

  /* UseEffect Hooks */
  useEffect(() => {
    const fetchProducts = async () => {
      const snapshot = await firebase
        .firestore()
        .collection("products")
        .where("active", "==", true)
        .get();

      const prod = {};
      for (let productDoc of snapshot.docs) {
        prod[productDoc.id] = productDoc.data();
        const priceSnapShot = await productDoc.ref.collection("prices").get();
        prod[productDoc.id].prices = priceSnapShot.docs.map(priceDoc => ({
          priceId: priceDoc.id,
          priceData: priceDoc.data(),
        }));
      }

      setProducts(prod);
    };

    const fetchDiscountInfo = async () => {
      if (discount_code) {
        try {
          const couponSnapshot = await firebase
            .firestore()
            .collection("coupons")
            .where("id", "==", discount_code)
            .get();

          if (!couponSnapshot.empty) {
            const couponData = couponSnapshot.docs[0].data();
            setDiscountInfo({
              active: couponData.active,
              percent_off: couponData.coupon.percent_off,
              amount_off: couponData.coupon.amount_off,
            });
          } else {
            console.error('Invalid discount code');
          }
        } catch (error) {
          console.error('Error fetching discount information:', error);
        }
      }
    };

    fetchProducts();
    fetchDiscountInfo();
  }, [discount_code]);

  useEffect(() => {
    if (CTA === "Jetzt anmelden") {
      setCardOrder(["Creator", "Starter", "Kostenlos"]);
    } else {
      setCardOrder(["Starter", "Creator"]);
    }
    if (products?.length !== 0) {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  if (loading === true || products?.length === 0)
    return (
      <SpinningLoader />
    );

  return (
    <section className={`${bgImage && "lg:py-20"} relative`} id="preis">
      <div className="">
        <h1 className="text-[26px] text-primary md:text-[48px] text-center font-semibold w-full md:w-10/12 mx-auto md:leading-[54px]">Zeit sparen mit KI-Text: Speziell auf den DACH-Markt zugeschnitten</h1>
        <p className="text-[16px] text-center w-11/12 md:w-9/12 md:text-[20px] mx-auto pt-[30px] pb-5 md:pb-[38px]">Erstelle mühelos Texte in Sekunden – bereits über 10.000 Kunden nutzen unsere KI-Software, die speziell für den DACH-Markt entwickelt wurde</p>
      </div>
      <div className={`${bgImage && "bg-dots-pattern py-8"}`}>
        <Container>
          <div className="mt-6 md:mt-4 align-center flex justify-center">
            <ul className={"grid grid-cols-1 lg:grid-cols-3 gap-5 xl:grid-cols-3 mx-2 sm:mx-6 md:mx-5 2xl:mx-16"}>
              <li key={`plan-2`}>
                <PlanCard
                  createUserAndCheckout={createUserAndCheckout3}
                  id={
                    getPricesByPeriod(
                      enabled === true ? "year" : "month",
                      getPlanByRole(cardOrder[0])?.prices
                    )?.priceId
                  }
                  mostPopular={prices[cardOrder[0]].mostPopular}
                  enabled={enabled}
                  title2={getPlanByRole(cardOrder[0])}
                  price={
                    getPricesByPeriod(
                      enabled === true ? "year" : "month",
                      getPlanByRole(cardOrder[0])?.prices
                    )?.priceData?.unit_amount / 100
                  }
                  monthlyPrice={getPricesByPeriod("month",
                    getPlanByRole(cardOrder[0])?.prices
                  )?.priceData?.unit_amount / 100
                  }
                  discountedPrice={calculateDiscountedPrice(
                    getPricesByPeriod(
                      enabled === true ? "year" : "month",
                      getPlanByRole(cardOrder[0])?.prices
                    )?.priceData?.unit_amount / 100
                  )}
                  planIncludes={prices[cardOrder[0]].text}
                  words={getPlanByRole(cardOrder[0])?.words}
                  CTA={CTA}
                />
              </li>
              <li key={`plan-1`}>
                <PlanCard
                  createUserAndCheckout={createUserAndCheckout2}
                  id={
                    getPricesByPeriod(
                      enabled === true ? "year" : "month",
                      getPlanByRole(cardOrder[1])?.prices
                    )?.priceId
                  }
                  mostPopular={prices[cardOrder[1]].mostPopular}
                  enabled={enabled}
                  title2={getPlanByRole(cardOrder[1])}
                  price={
                    getPricesByPeriod(
                      enabled === true ? "year" : "month",
                      getPlanByRole(cardOrder[1])?.prices
                    )?.priceData?.unit_amount / 100
                  }
                  discountedPrice={calculateDiscountedPrice(
                    getPricesByPeriod(
                      enabled === true ? "year" : "month",
                      getPlanByRole(cardOrder[1])?.prices
                    )?.priceData?.unit_amount / 100
                  )}
                  monthlyPrice={calculateDiscountedPrice(
                    getPricesByPeriod(
                      "month",
                      getPlanByRole(cardOrder[1])?.prices
                    )?.priceData?.unit_amount / 100
                  )}
                  planIncludes={prices[cardOrder[1]].text}
                  words={getPlanByRole(cardOrder[1])?.words}
                  CTA={CTA}
                />
              </li>
              {/* TeamCard */}
              <li key={`plan-3`}>
                <TeamPlanCard
                  title={teamCarddata.title}
                  planIncludes={teamCarddata.text}
                />
              </li>
              {cardOrder[2] && (
                <li key={`plan-0`}>
                  <PlanCard
                    createUserAndCheckout={createUserAndCheckout1}
                    id={
                      getPricesByPeriod(
                        enabled === true ? "year" : "month",
                        getPlanByRole(cardOrder[2])?.prices
                      )?.priceId
                    }
                    mostPopular={prices[cardOrder[2]].mostPopular}
                    enabled={enabled}
                    title2={getPlanByRole(cardOrder[2])}
                    price={
                      getPricesByPeriod(
                        enabled === true ? "year" : "month",
                        getPlanByRole(cardOrder[2])?.prices
                      )?.priceData?.unit_amount / 100
                    }
                    discountedPrice={calculateDiscountedPrice(
                      getPricesByPeriod(
                        enabled === true ? "year" : "month",
                        getPlanByRole(cardOrder[2])?.prices
                      )?.priceData?.unit_amount / 100
                    )}
                    planIncludes={prices[cardOrder[2]].text}
                    words={getPlanByRole(cardOrder[2])?.words}
                    CTA={CTA}
                  />
                </li>
              )}
            </ul>
          </div>
        </Container>
      </div>
    </section>
  );
};

const prices = {
  "Starter": {
    "text": [
      "KI-Chat",
      "Mehr als 50 KI-Vorlagen für alle gängigen Schreibaufgaben",
      "Das Beste Robowriter-KI-Modell",
      "100 Texte / Monat",
      "3 eigene Tonfälle",
      "Kundensupport"
    ],
  },
  "Creator": {
    "text": [
      "KI-Chat",
      "Mehr als 50 KI-Vorlagen für alle gängigen Schreibaufgaben",
      "Das Beste Robowriter-KI-Modell",
      "Unlimitiert Texte generieren",
      "Unlimitierte Tonfälle",
      "Kundensupport"
    ],
  },
}

const teamCarddata = {
  "title": "Team Lizenz",
  "text": [
    "Daten kontenübergreifend verwalten",
    "Individuelle Compliance-Vorgaben",
    "Single Sign-on (SSO)",
    "24/7 live support",
    "Robowriter-Funktionen ohne Einschränkungen nutzen"
  ]
}

export default PlansSection;
